import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Paper, CircularProgress, Alert, Fade, Slide } from '@mui/material';
import { doc, onSnapshot, updateDoc, increment } from 'firebase/firestore';
import { db } from '../firebase';
import IntroductionSlide from './slides/IntroductionSlide';
import CreditAnalysisSlide from './slides/CreditAnalysisSlide';
import InterestCalculatorSlide from './slides/InterestCalculatorSlide';
import TermOptionsSlide from './slides/TermOptionsSlide';
import LogisticsSlide from './slides/LogisticsSlide';
import SummarySlide from './slides/SummarySlide';
import AdditionalInfoSlide from './slides/AdditionalInfoSlide';
import ProgramSummarySlide from './slides/ProgramSummarySlide';
import CursorControl from './CursorControl';
import { RootState } from '../store';
import { useDispatch } from 'react-redux';
import { setCurrentSlide, updateCursorPosition, toggleCursorControl, setSelectedTerms, setAdditionalPayment, setHoveredProgramStep, updateCalculations } from '../store/presentationSlice';

const ClientView: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [presentationData, setPresentationData] = useState<RootState['presentation'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'right' | 'left'>('right');
  const [cursorControlEnabled, setCursorControlEnabled] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    console.log('Connecting to session:', sessionId);
    setLoading(true);
    setError(null);

    // Update connected clients count
    const sessionRef = doc(db, 'presentations', sessionId);
    updateDoc(sessionRef, {
      connectedClients: increment(1)
    }).catch(error => {
      console.error('Failed to update client count:', error);
    });

    let previousSlide = -1;
    const unsubscribe = onSnapshot(doc(db, 'presentations', sessionId), (doc) => {
      if (doc.exists()) {
        const data = doc.data() as RootState['presentation'];
        
        // Determine slide transition direction
        if (previousSlide !== -1 && data.currentSlide !== previousSlide) {
          setSlideDirection(data.currentSlide > previousSlide ? 'left' : 'right');
          dispatch(setCurrentSlide(data.currentSlide));
        }
        previousSlide = data.currentSlide;
        
        // Update all presentation data
        setPresentationData(data);
        
        // Update term length if changed
        if (data.selectedTerms !== undefined) {
          dispatch(setSelectedTerms(data.selectedTerms));
        }

        // Update calculations if changed
        if (data.calculations) {
          dispatch(updateCalculations(data.calculations));
        }

        // Update cursor position with debouncing
        if (data.cursorPosition) {
          requestAnimationFrame(() => {
            dispatch(updateCursorPosition(data.cursorPosition));
          });
        }

        // Update cursor control state only when it changes
        if (data.cursorControlEnabled !== undefined && 
            data.cursorControlEnabled !== cursorControlEnabled) {
          dispatch(toggleCursorControl());
          setCursorControlEnabled(data.cursorControlEnabled);
        }
        
        // Update additional payment if changed
        if (data.additionalPayment !== undefined) {
          dispatch(setAdditionalPayment(data.additionalPayment));
        }

        // Update hovered program step if changed
        if (data.hoveredProgramStep !== undefined) {
          console.log('Client received hoveredProgramStep:', data.hoveredProgramStep);
          dispatch(setHoveredProgramStep(data.hoveredProgramStep));
        }
        
        setConnected(true);
        setLoading(false);
        setError(null);
      } else {
        setError('Session not found');
        setLoading(false);
      }
    }, (error) => {
      console.error('Error connecting to session:', error);
      setError('Failed to connect to session');
      setLoading(false);
    });

    // Update presenter that client has joined
    const updatePresenterStatus = async () => {
      try {
        const docRef = doc(db, 'presentations', sessionId);
        await fetch(`${window.location.origin}/api/notify-presenter`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ sessionId }),
        });
      } catch (error) {
        console.error('Failed to notify presenter:', error);
      }
    };

    updatePresenterStatus();

    // Cleanup function
    return () => {
      unsubscribe();
      // Decrement connected clients count when component unmounts
      updateDoc(sessionRef, {
        connectedClients: increment(-1)
      }).catch(error => {
        console.error('Failed to update client count on disconnect:', error);
      });
    };
  }, [sessionId, dispatch, cursorControlEnabled]);

  const renderSlide = () => {
    if (!presentationData) return null;

    const slideContent = (() => {
      switch (presentationData.currentSlide) {
        case 0:
          return <IntroductionSlide />;
        case 1:
          return <CreditAnalysisSlide />;
        case 2:
          return <InterestCalculatorSlide />;
        case 3:
          return <TermOptionsSlide />;
        case 4:
          return <LogisticsSlide />;
        case 5:
          return <ProgramSummarySlide isPresenter={false} />;
        case 6:
          return <AdditionalInfoSlide />;
        default:
          return <IntroductionSlide />;
      }
    })();

    return (
      <Slide direction={slideDirection} in={true} mountOnEnter unmountOnExit>
        <div>{slideContent}</div>
      </Slide>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={60} />
        <Fade in={true} timeout={1000}>
          <Alert severity="info">Connecting to presentation...</Alert>
        </Fade>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'relative' }}>
      <Container maxWidth="lg" sx={{ 
        py: 4,
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ mt: 4, mb: 4 }}>
          {!connected && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Reconnecting to presentation...
            </Alert>
          )}
          <Paper 
            elevation={3} 
            className="presentation-container"
            sx={{ 
              p: 3, 
              minHeight: '80vh',
              position: 'relative'
            }}
          >
            {renderSlide()}
          </Paper>
        </Box>
      </Container>
      <CursorControl isPresenter={false} />
    </Box>
  );
};

export default ClientView;
